import React, { useEffect, useState } from 'react';
import { list, downloadData } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress, Divider, IconButton, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import S3Image from '../../components/S3Image';
import { useDeviceData } from '../../context/DeviceDataContext';
import { useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import { deleteImage } from '../../utils/clientUtils';

async function listAllImages(company) {
    const result = await list({
        path: `gtms4/company=${company}/`,
        options: { listAll: true, bucket: 'upload' },
    });
    return result?.items?.map(item => item.path) ?? [];
}

function parseImagePath(path) {
    // e.g. gtms4/company=testcompany/device=1/board=2/image.jpg
    const deviceMatch = path.match(/device=([^/]+)/);
    const boardMatch = path.match(/board=([^/]+)/);
    return {
        dtid: deviceMatch?.[1] ?? '?',
        board: boardMatch?.[1] ? Number(boardMatch[1]) : '?',
        filename: path.split('/').pop(),
        path,
    };
}

// Returns: { [dtid]: { [board]: [{ path, filename }] } }
function groupImages(paths) {
    const groups = {};
    for (const path of paths) {
        const { dtid, board, filename } = parseImagePath(path);
        if (!groups[dtid]) groups[dtid] = {};
        if (!groups[dtid][board]) groups[dtid][board] = [];
        groups[dtid][board].push({ path, filename });
    }
    for (const dtid of Object.keys(groups))
        for (const board of Object.keys(groups[dtid]))
            groups[dtid][board].sort((a, b) => a.filename.localeCompare(b.filename));
    return groups;
}

function buildDeviceLookup(devicesData) {
    // { [dtid]: { name, boards: { [position]: { width, height } } } }
    const lookup = {};
    for (const type of devicesData?.devices?.types ?? []) {
        const boards = {};
        for (const comp of type.components ?? []) {
            if (!boards[comp.position]) {
                boards[comp.position] = { width: comp.width, height: comp.height };
            }
        }
        lookup[String(type.dtid)] = { name: type.devicename, boards };
    }
    return lookup;
}

async function downloadAndZip(paths) {
    const zip = new JSZip();
    await Promise.all(paths.map(async (path) => {
        const result = await downloadData({ path, options: { bucket: 'upload' } }).result;
        const blob = await result.body.blob();
        // Preserve folder structure: strip leading "gtms4/" prefix
        const zipPath = path.replace(/^gtms4\//, '');
        zip.file(zipPath, blob);
    }));
    const content = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(content);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `gtms-images-${date}.zip`;
    a.click();
    URL.revokeObjectURL(url);
}

function ImageList() {
    const { t } = useTranslation();
    const { devicesData, loading: deviceLoading } = useDeviceData();
    const navigate = useNavigate();
    const [imagePaths, setImagePaths] = useState(null);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [deletingKey, setDeletingKey] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [deleteInProgress, setDeleteInProgress] = useState(false);

    useEffect(() => {
        async function load() {
            try {
                const session = await fetchAuthSession();
                const company = session.tokens?.idToken?.payload?.['custom:company'];
                if (!company) throw new Error('Company not found in session');
                const groupsRaw = session.tokens?.idToken?.payload?.['cognito:groups'] ?? '';
                const groups = Array.isArray(groupsRaw)
                    ? groupsRaw
                    : String(groupsRaw).replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
                setCanDelete(groups.includes('image_management'));
                setImagePaths(await listAllImages(company));
            } catch (e) {
                setError(e.message);
            }
        }
        load();
    }, []);

    if (error) return <Typography color="error">{error}</Typography>;
    if (imagePaths === null || deviceLoading) return <CircularProgress size={24} />;
    if (imagePaths.length === 0) return <Typography color="text.secondary">{t('imageList.noImages')}</Typography>;

    const deviceLookup = buildDeviceLookup(devicesData);
    const groups = groupImages(imagePaths);

    const handleDownloadAll = async () => {
        setDownloading(true);
        try {
            await downloadAndZip(imagePaths);
        } finally {
            setDownloading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        setDeleteInProgress(true);
        setDeleteError(null);
        try {
            await deleteImage(deletingKey);
            setImagePaths(prev => prev.filter(p => p !== deletingKey));
            setDeletingKey(null);
        } catch (e) {
            setDeleteError(e.message ?? t('imageList.failedToDelete'));
        } finally {
            setDeleteInProgress(false);
        }
    };

    const deletingFilename = deletingKey?.split('/').pop();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {Object.entries(groups).map(([dtid, boardGroups], deviceIdx) => {
                const deviceInfo = deviceLookup[dtid];
                const deviceName = deviceInfo?.name ?? `Device ${dtid}`;

                return (
                    <Box key={dtid}>
                        {deviceIdx > 0 && <Divider sx={{ mb: 3 }} />}
                        <Typography variant="h2" sx={{ color: 'primary.main', mb: 2 }}>
                            {deviceName}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {Object.entries(boardGroups).map(([board, images], boardIdx) => {
                                const boardInfo = deviceInfo?.boards?.[Number(board)];
                                const boardHeight = boardInfo?.height ?? 64;
                                const boardLabel = boardInfo
                                    ? t('imageList.boardLabel', { number: Number(boardIdx) + 1, width: boardInfo.width, height: boardInfo.height })
                                    : t('imageList.boardLabelFallback', { board });

                                return (
                                    <Box key={board}>
                                        <Typography variant="h3" sx={{ color: 'text.secondary', mb: 1 }}>
                                            {boardLabel}
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                                            {images.map(({ path, filename }) => (
                                                <Box
                                                    key={path}
                                                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                                                >
                                                    <Box sx={{ position: 'relative', '&:hover .delete-btn': { opacity: 1 } }}>
                                                        <S3Image
                                                            src={path}
                                                            boardHeight={boardHeight}
                                                            sx={{ height: boardHeight, marginLeft: 0, marginTop: 0 }}
                                                        />
                                                        {canDelete && (
                                                            <Tooltip title={t('imageList.deleteImageTooltip')}>
                                                                <IconButton
                                                                    className="delete-btn"
                                                                    size="small"
                                                                    onClick={() => { setDeleteError(null); setDeletingKey(path); }}
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        right: 0,
                                                                        padding: '2px',
                                                                        opacity: 0,
                                                                        transition: 'opacity 0.15s',
                                                                        bgcolor: 'rgba(0,0,0,0.5)',
                                                                        color: 'white',
                                                                        '&:hover': { bgcolor: 'rgba(180,0,0,0.8)' },
                                                                    }}
                                                                >
                                                                    <CloseIcon sx={{ fontSize: 14 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    >
                                                        {filename}
                                                    </Typography>
                                                </Box>
                                            ))}
                                            {canDelete && (
                                                <Tooltip title={t('imageList.uploadImageTooltip')}>
                                                    <IconButton
                                                        onClick={() => navigate(`/images/upload?device=${dtid}&board=${board}`)}
                                                        sx={{ height: boardHeight, width: boardHeight, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}
                                                    >
                                                        <AddCircleOutlineIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                );
            })}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                    onClick={handleDownloadAll}
                    disabled={downloading || imagePaths.length === 0}
                >
                    {downloading ? t('imageList.preparing') : t('imageList.downloadAll')}
                </Button>
            </Box>

            <Dialog open={deletingKey !== null} onClose={() => !deleteInProgress && setDeletingKey(null)}>
                <DialogTitle>{t('imageList.deleteImageTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('imageList.deleteConfirm', { filename: deletingFilename })}
                    </DialogContentText>
                    {deleteError && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>{deleteError}</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeletingKey(null)} disabled={deleteInProgress}>{t('common.cancel')}</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        disabled={deleteInProgress}
                        startIcon={deleteInProgress ? <CircularProgress size={16} /> : null}
                    >
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ImageList;
